import { PointerEvent } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { RootState } from "app/store";
import { DialogType } from "app/types";
import { actions } from "app/configSlice";
import I18n from "common/i18n";
import { useScale, btnDelay, usePlaySound, shouldApplyGuestRestrictions } from "common/utils";
import { IconBack } from "features/user/assets";
import { usePlan, PlanType } from "features/subscription";
import { selectAllChatRoomMessageSync } from "features/user/slice";
import { actions as creatorActions } from "features/creator/slice";
import { AccountType } from "./types";

const getPlanColor = (plan: PlanType): string => {
  switch (plan) {
    case PlanType.TRIAL:
      return "#9E9E9E";
    case PlanType.FREE:
      return "#4C9DF1";
    case PlanType.EDUCATION:
      return "#3E5878";
    case PlanType.STANDARD:
      return "#66BB6A";
    case PlanType.PREMIUM:
      return "#FF5722";
    case PlanType.BUILDER:
      return "#9C27B0";
    default:
      return "#9E9E9E";
  }
};

export const UserBtn = () => {
  const play = usePlaySound();
  const plan = usePlan();
  const { scale } = useScale();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const active = useSelector((state: RootState) => state.user.active);
  const parent = useSelector((state: RootState) => state.user.parent);
  const chatRoomMessageSync = useSelector(selectAllChatRoomMessageSync);
  const hasUnreadMessage =
    chatRoomMessageSync.filter(
      (sync) =>
        dayjs(sync.latestMessageCreatedAt, "YYYY-MM-DDTHH:mm:ssZ").diff(
          dayjs(sync.latestReadAt, "YYYY-MM-DDTHH:mm:ssZ"),
        ) > 0 ||
        (sync.latestReadAt === null && sync.latestMessageCreatedAt !== null),
    ).length > 0;

  const isSakidori =
    parent?.account_type === AccountType.GUEST &&
    parent?.created_at &&
    !shouldApplyGuestRestrictions(parent.created_at);

  const planColor = isSakidori ? "#9575CD" : getPlanColor(plan);

  const handleClick = (e: PointerEvent) => {
    e.stopPropagation();
    play();
    btnDelay(() => {
      dispatch(
        actions.updateDialog({
          type: DialogType.USER_PROFILE,
          value: true,
        }),
      );
    });
  };

  const handleBack = () => {
    play();
    if (location.pathname !== "/") {
      const pathArray = location.pathname.split("/");
      switch (true) {
        case /top/.test(location.pathname):
          if (active?.account_type !== AccountType.GUEST) {
            navigate("/", { replace: true });
          }
          break;
        case /\/user/.test(location.pathname):
          navigate("/top");
          break;
        case /\/creator$/.test(location.pathname):
          navigate("/top");
          break;
        case /\/creator\//.test(location.pathname):
          btnDelay(() => dispatch(creatorActions.updateCapture(true)));
          break;
        case /\/courses$/.test(location.pathname):
          navigate("/top");
          break;
        case /\/courses\/[a-zA-Z0-9_]+\/stage\/\d$/.test(location.pathname):
          navigate("/courses");
          break;
        case /\/courses\/[a-zA-Z0-9_]+\/stage\/\d\/step\/\d$/.test(location.pathname):
          navigate(`/courses/${pathArray[2]}/stage/${pathArray[4]}`);
          break;
        case /\/courses\/[a-zA-Z0-9_]+/.test(location.pathname):
          navigate(`/courses`);
          break;
        default:
          break;
      }
    }
  };

  return (
    <div className="flex-row-el absolute left-0 z-[9998]" style={{ top: 4 * scale }}>
      <div
        className="flex-row-el items-center justify-start"
        style={{ width: 170 * scale, height: 102 * scale }}
      >
        <div
          className="flex-row-el items-center justify-start w-[170px] h-[102px] origin-left"
          style={{ transform: `scale(${scale})` }}
        >
          <div
            id="app-back-btn"
            className={`flex-col-el items-start justify-center w-[96px] h-[72px] pl-2 cursor-pointer active:opacity-70 ${
              active?.account_type === AccountType.GUEST
                ? location.pathname === "/top"
                  ? "bg-gray2-60"
                  : "bg-brown-100"
                : location.pathname === "/"
                  ? "bg-gray2-60"
                  : "bg-brown-100"
            }`}
            onClick={handleBack}
          >
            <div className="flex-col-center">
              <div className="flex-col-center w-[32px] h-[32px] mt-1 mb-1">
                <IconBack />
              </div>

              <p className="type-text type-sm text-white">{I18n.t("MSG_BACK_BTN")}</p>
            </div>
          </div>

          <div
            className="flex-col-el flex-center absolute left-[64px] bg-white rounded-full z-10"
            style={{
              width: 104,
              height: 104,
              filter: "drop-shadow(1px 1px 2px rgba(63, 74, 97, 0.4))",
            }}
          >
            {hasUnreadMessage && (
              <div className="flex-col-el flex-center absolute right-0 top-0 bg-danger rounded-full w-[30px] h-[30px] z-50" />
            )}

            <div
              className="flex-col-center w-[85%] h-[85%] rounded-full overflow-hidden cursor-pointer active:scale-95"
              onPointerDown={handleClick}
            >
              <img
                key={active?.icon}
                alt="プレイヤープロフィール画像"
                src={active?.icon}
                className="w-full h-full object-contain"
              />
            </div>

            <div
              className="flex-row-el items-center absolute -bottom-5 h-[36px] w-[132px] rounded-full overflow-hidden bg-white shadow-[-1px_1px_1px_rgba(63,74,97,0.15),1px_1px_3px_rgba(63,74,97,0.25)] cursor-pointer active:scale-95"
              onPointerDown={handleClick}
            >
              <div
                className="flex-col-center rounded-full flex-shrink-0"
                style={{ backgroundColor: planColor, width: 36, height: 36 }}
              >
                <span className="material-symbols-outlined type-body text-white">
                  workspace_premium
                </span>
              </div>
              <p className="text-body type-sm text-textcolor-black text-body-ellipsis flex-1 min-w-0 pl-2 pr-4 text-center whitespace-nowrap leading-tight">
                {active?.display_name}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
